import { useCallback, useEffect, useMemo, useState } from 'react'
import { FlaskConical, Play } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, Field } from '@/components/ui/input'
import { useToast } from '@/providers/UiProviders'
import {
  fetchExecutions,
  fetchWorkflows,
  runWorkflow,
  AUTO_STR as S,
  type ExecutionRow,
  type WorkflowRow,
} from './automationApi'

/** Small dialog: pick a workflow and fire a real test run (`POST .../run`). */
function TestRunDialog({
  open,
  onClose,
  onRan,
}: {
  open: boolean
  onClose: () => void
  onRan: () => void
}) {
  const { toast } = useToast()
  const [workflows, setWorkflows] = useState<WorkflowRow[]>([])
  const [selected, setSelected] = useState<string>('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    const ac = new AbortController()
    fetchWorkflows({ filters: [], sort: [], page: 0, pageSize: 500, search: '', signal: ac.signal })
      .then((res) => {
        setWorkflows(res.rows)
        const first = res.rows[0]
        setSelected(first ? String(first.id) : '')
      })
      .catch(() => setWorkflows([]))
    return () => ac.abort()
  }, [open])

  async function run() {
    const id = Number(selected)
    if (!id) return
    setBusy(true)
    try {
      await runWorkflow(id)
      toast(S.testStarted, { tone: 'success' })
      onRan()
      onClose()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={S.runTest}
      icon={<FlaskConical size={16} className="text-accent" />}
      footer={
        <>
          <Button variant="primary" disabled={busy || !selected} onClick={run}>
            {busy ? S.saving : S.runTest}
          </Button>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {S.cancel}
          </Button>
        </>
      }
    >
      <Field label={S.selectWorkflow}>
        <Select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={workflows.length === 0}
        >
          {workflows.length === 0 ? (
            <option value="">{S.noWorkflows}</option>
          ) : (
            workflows.map((w) => (
              <option key={w.id} value={String(w.id)}>
                {w.name}
              </option>
            ))
          )}
        </Select>
      </Field>
    </Dialog>
  )
}

/**
 * A26.04 — Тестовый режим (dry-run / test mode). Fires a real single run of a
 * workflow (`POST .../run`) and shows the resulting execution log (`.../executions`)
 * with status/duration/error. Placeholder permission USERS_VIEW.
 */
export function TestModeScreen() {
  const [runOpen, setRunOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<ExecutionRow>[]>(
    () => [
      { key: 'workflow', header: S.workflow, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'started_at', header: S.startedAt, type: 'text' },
      { key: 'duration_ms', header: S.duration, type: 'number' },
      { key: 'error', header: S.error, type: 'text' },
    ],
    [],
  )

  const fetcher = useCallback(
    (p: Parameters<typeof fetchExecutions>[0]) => {
      void reloadKey
      return fetchExecutions(p)
    },
    [reloadKey],
  )

  return (
    <>
      <PageHeader
        title={S.testMode}
        screenId="A26.04"
        description={S.testResults}
        actions={
          <Button variant="primary" size="sm" onClick={() => setRunOpen(true)}>
            <Play size={13} />
            {S.runTest}
          </Button>
        }
      />
      <DataTable<ExecutionRow>
        screenId="A26.04"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <FlaskConical size={28} strokeWidth={1.5} /> }}
      />
      <TestRunDialog
        open={runOpen}
        onClose={() => setRunOpen(false)}
        onRan={() => setReloadKey((k) => k + 1)}
      />
    </>
  )
}
